import { ReactNode } from "react";
import { Radio } from "lucide-react";
import type { LunimarRealm } from "@/components/lunimar/lunimarRealms";

interface Props {
  realm?: LunimarRealm;
  title: string;
  intent?: string;
  /** Tiny label above title — e.g. "Mission 02" or "Scenario A". */
  eyebrow?: string;
  children?: ReactNode;
}

/**
 * Mission-briefing card. Use at the top of a decision step to set context
 * before showing the choice grid. Renders an accent-tinted glass panel
 * with a small "transmission" eyebrow.
 */
const ScenarioBrief = ({ realm, title, intent, eyebrow, children }: Props) => {
  const accent = realm?.primaryAccent ?? "210 90% 64%";
  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-4 backdrop-blur-md sm:p-5"
      style={{
        borderColor: `hsl(${accent} / 0.3)`,
        background: `linear-gradient(135deg, hsl(${accent} / 0.10), hsl(228 45% 12% / 0.6))`,
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-30 blur-3xl"
        style={{ background: `hsl(${accent})` }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
             style={{ color: `hsl(${accent})` }}>
          <Radio className="h-3 w-3" />
          {eyebrow ?? "Incoming transmission"}
        </div>
        <h3 className="mt-2 font-heading text-base font-bold text-white sm:text-lg">
          {title}
        </h3>
        {intent && (
          <p className="mt-1.5 text-xs leading-relaxed text-white/75 sm:text-sm">
            {intent}
          </p>
        )}
        {children && <div className="mt-3">{children}</div>}
      </div>
    </div>
  );
};

export default ScenarioBrief;